import React, { useState, useRef, useEffect } from 'react';
import EstimationIndicator from '/src/components/Reusable/EstimationIndicator';
import AppMagnittLoader from '/src/components/Reusable/AppMagnittLoader';
import { SortIcon, DeploymentCell, StatusCell } from '../TableCells';
import { COLUMNS } from '../../constants';
import { getAmountValue } from '../../helpers';
import { getInitials, MOB_BADGE_COLORS } from '/src/components/ICESections/Shared/mobileListUtils';
import { SectionEmptyState, SectionErrorState } from '/src/components/ICESections/Shared/SectionEmptyState';

const FundsRaisedTableGrid = ({ rows, isLoading, hasError, sortKey, sortDir, onSort, onLpClick, onFundClick = null }) => {
    const [scrollPos, setScrollPos] = useState(0);
    const [maxScroll, setMaxScroll] = useState(null);
    const [stickyColWidth, setStickyColWidth] = useState(180);
    const [expandedMobileRows, setExpandedMobileRows] = useState([]);
    const wrapperRef = useRef(null);

    const toggleMobileRow = (id) => setExpandedMobileRows(prev =>
        prev.includes(id) ? prev.filter(k => k !== id) : [...prev, id]
    );

    useEffect(() => {
        const el = wrapperRef.current;
        if (!el) return;
        const update = () => {
            setScrollPos(el.scrollLeft);
            setMaxScroll(el.scrollWidth - el.clientWidth);
            // Keep left-button position locked to the actual Fund column right edge
            const firstTh = el.querySelector('th:first-child');
            if (firstTh) setStickyColWidth(firstTh.offsetWidth);
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [rows]);

    const handleScroll = () => {
        const el = wrapperRef.current;
        if (!el) return;
        setScrollPos(el.scrollLeft);
        setMaxScroll(el.scrollWidth - el.clientWidth);
    };

    const scrollTable = (direction) => {
        const el = wrapperRef.current;
        if (!el) return;
        const scrollVal = Math.max(el.clientWidth - stickyColWidth, 150);
        el.scrollLeft += direction === 'left' ? -scrollVal : scrollVal;
    };

    return (
        <div className="funds-raised-table-shell">

            <EstimationIndicator />

            {/* ── Desktop table ── */}
            <div className="funds-raised-scroll-area">
                {scrollPos > 0 && (
                    <button
                        className="funds-raised-scroll-btn"
                        style={{ left: stickyColWidth }}
                        onClick={() => scrollTable('left')}
                        aria-label="Scroll left"
                    >
                        <i className="fas fa-chevron-left" />
                    </button>
                )}
                {maxScroll > 0 && scrollPos < maxScroll && (
                    <button
                        className="funds-raised-scroll-btn funds-raised-scroll-btn--right"
                        onClick={() => scrollTable('right')}
                        aria-label="Scroll right"
                    >
                        <i className="fas fa-chevron-right" />
                    </button>
                )}
                <div className="funds-raised-scroll" ref={wrapperRef} onScroll={handleScroll}>
                    <table className="funds-raised-table">
                        <thead>
                            <tr className="funds-raised-table__header">
                                {COLUMNS.map((col) => (
                                    <th
                                        key={col.key}
                                        onClick={col.sortable ? () => onSort(col.key) : undefined}
                                        className={[
                                            col.thClass || '',
                                            col.sortable ? 'funds-raised-table__th--sortable' : '',
                                            col.key === sortKey ? 'funds-raised-table__th--active' : '',
                                        ].filter(Boolean).join(' ')}
                                        aria-sort={
                                            col.key === sortKey
                                                ? sortDir === 'asc' ? 'ascending' : 'descending'
                                                : undefined
                                        }
                                    >
                                        {col.checkIcon
                                            ? <i className="fa fa-check funds-raised-table__check-icon" aria-label="Verified" />
                                            : <span>{col.label}</span>
                                        }
                                        {col.sortable && (
                                            <SortIcon column={col.key} sortKey={sortKey} sortDir={sortDir} />
                                        )}
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan={COLUMNS.length} className="funds-raised-table__td--no-pad">
                                        <AppMagnittLoader data={{ height: '300px', background: '#fff', padding: '40px' }} />
                                    </td>
                                </tr>
                            ) : hasError ? (
                                <tr>
                                    <td colSpan={COLUMNS.length} className="funds-raised-table__td--no-pad">
                                        <SectionErrorState />
                                    </td>
                                </tr>
                            ) : !rows.length ? (
                                <tr>
                                    <td colSpan={COLUMNS.length} className="funds-raised-table__td--no-pad">
                                        <SectionEmptyState>No funds found.</SectionEmptyState>
                                    </td>
                                </tr>
                            ) : (
                                rows.map((row) => (
                                    <tr key={row.id} className={row.isBalanceSheet ? 'funds-raised-table__row--balance-sheet' : undefined}>
                                        {/* Fund */}
                                        <td data-label="Fund">
                                            {onFundClick ? (
                                                <button
                                                    type="button"
                                                    className="funds-raised-table__fund-link funds-raised-table__fund-link--btn"
                                                    onClick={() => onFundClick(row.id)}
                                                >
                                                    {row.name}
                                                </button>
                                            ) : (
                                                <span className="funds-raised-table__fund-name">
                                                    {row.name}
                                                </span>
                                            )}
                                        </td>

                                        {/* Vintage */}
                                        <td data-label="Vintage">
                                            {row.vintage != null
                                                ? row.vintage
                                                : <span className="funds-raised-table__empty">–</span>}
                                        </td>

                                        {/* Funds Raised */}
                                        <td data-label="Funds Raised" className="funds-raised-table__amount-cell">
                                            {row.fundSize
                                                ? <strong>{getAmountValue(row.fundSize)}</strong>
                                                : row.isBalanceSheet
                                                    ? <span className="funds-raised-table__empty">–</span>
                                                    : <span>Undisclosed</span>}
                                        </td>

                                        {/* Deployed */}
                                        <td data-label="Deployed" className="funds-raised-table__amount-cell">
                                            {row.deployed
                                                ? <span className={row.isDeployedEstimated ? 'amount--estimated' : ''}>{getAmountValue(row.deployed)}</span>
                                                : row.isBalanceSheet
                                                    ? <span className="funds-raised-table__empty">–</span>
                                                    : <span>Undisclosed</span>}
                                        </td>

                                        {/* Deployment progress */}
                                        <td data-label="Deployment">
                                            <DeploymentCell pct={row.deploymentPct} color={row.deploymentColor} isEstimated={row.isDeploymentPctEstimated} />
                                        </td>

                                        {/* # Companies */}
                                        <td data-label="# Companies">
                                            {row.portfolioCompanies != null
                                                ? row.portfolioCompanies
                                                : <span className="funds-raised-table__empty">–</span>}
                                        </td>

                                        {/* # Exits */}
                                        <td data-label="# Exits">
                                            {row.exitsCount != null
                                                ? row.exitsCount
                                                : <span className="funds-raised-table__empty">–</span>}
                                        </td>

                                        {/* LPs */}
                                        <td data-label="LPs">
                                            {row.lpCount != null ? (
                                                <button
                                                    className="funds-raised-table__lps funds-raised-table__lps--clickable"
                                                    onClick={() => onLpClick(row)}
                                                >
                                                    {row.lpCount} {row.lpCount === 1 ? 'LP' : 'LPs'}
                                                </button>
                                            ) : (
                                                <span className="funds-raised-table__empty">–</span>
                                            )}
                                        </td>

                                        {/* Status */}
                                        <td data-label="Status">
                                            <StatusCell badges={row.statusBadges} />
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ── Mobile card list ── */}
            <div className="funds-raised-mobile-list">
                {isLoading ? (
                    <AppMagnittLoader data={{ height: '200px', background: '#fff', padding: '24px' }} />
                ) : hasError ? (
                    <SectionErrorState />
                ) : !rows.length ? (
                    <SectionEmptyState>No funds found.</SectionEmptyState>
                ) : (
                    rows.map((row, idx) => {
                        const isOpen = expandedMobileRows.includes(row.id);
                        const col = MOB_BADGE_COLORS[idx % MOB_BADGE_COLORS.length];
                        const firstBadge = row.statusBadges?.[0];

                        return (
                            <div
                                key={row.id}
                                className={`fr-mob-row${row.isBalanceSheet ? ' fr-mob-row--balance-sheet' : ''}`}
                                onClick={() => toggleMobileRow(row.id)}
                            >
                                <div className="fr-mob-row__top">
                                    <div className="fr-mob-badge" style={{ background: col.bg, color: col.txt }}>
                                        {getInitials(row.name)}
                                    </div>
                                    <div className="fr-mob-row__main">
                                        {onFundClick ? (
                                            <button
                                                type="button"
                                                className="funds-raised-table__fund-link funds-raised-table__fund-link--btn"
                                                onClick={(e) => { e.stopPropagation(); onFundClick(row.id); }}
                                            >
                                                {row.name}
                                            </button>
                                        ) : (
                                            <span className="funds-raised-table__fund-name">{row.name}</span>
                                        )}
                                        <div className="fr-mob-row__meta">
                                            {row.vintage != null && <span>{row.vintage}</span>}
                                            {row.vintage != null && firstBadge && <span className="fr-mob-row__meta-sep">·</span>}
                                            {firstBadge && (
                                                <span className={`fr-status-badge fr-status-badge--${firstBadge.variant}`}>
                                                    {firstBadge.label}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="fr-mob-row__right">
                                        <span className="fr-mob-row__size">
                                            {row.fundSize ? getAmountValue(row.fundSize) : (row.isBalanceSheet ? '–' : 'Undisclosed')}
                                        </span>
                                        <div className="fr-mob-row__status-row">
                                            <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} fr-mob-row__chevron`} />
                                        </div>
                                    </div>
                                </div>

                                {isOpen && (
                                    <div className="fr-mob-row__expand">
                                        <div className="mob-expand-grid">
                                            <div className="mob-expand-cell">
                                                <div className="mob-expand-cell__lbl">Deployed</div>
                                                <div className="mob-expand-cell__val">
                                                    {row.deployed
                                                        ? <span className={row.isDeployedEstimated ? 'amount--estimated' : ''}>{getAmountValue(row.deployed)}</span>
                                                        : (row.isBalanceSheet ? '–' : 'Undisclosed')}
                                                </div>
                                            </div>
                                            <div className="mob-expand-cell">
                                                <div className="mob-expand-cell__lbl">Deployment %</div>
                                                <div className="mob-expand-cell__val">
                                                    {row.deploymentPct != null
                                                        ? <span className={row.isDeploymentPctEstimated ? 'amount--estimated' : ''}>{row.deploymentPct}%</span>
                                                        : '–'}
                                                </div>
                                            </div>
                                            <div className="mob-expand-cell">
                                                <div className="mob-expand-cell__lbl">Companies</div>
                                                <div className="mob-expand-cell__val">
                                                    {row.portfolioCompanies != null ? row.portfolioCompanies : '–'}
                                                </div>
                                            </div>
                                            <div className="mob-expand-cell">
                                                <div className="mob-expand-cell__lbl">Exits</div>
                                                <div className="mob-expand-cell__val">
                                                    {row.exitsCount != null ? row.exitsCount : '–'}
                                                </div>
                                            </div>
                                            {row.lpCount != null && (
                                                <div className="mob-expand-cell">
                                                    <div className="mob-expand-cell__lbl">LPs</div>
                                                    <div className="mob-expand-cell__val">
                                                        <button
                                                            className="funds-raised-table__lps funds-raised-table__lps--clickable"
                                                            onClick={(e) => { e.stopPropagation(); onLpClick(row); }}
                                                        >
                                                            {row.lpCount} LPs
                                                            <i className="fa fa-chevron-down" aria-hidden="true" />
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default FundsRaisedTableGrid;
